import { setupChameleon } from "PFApp/bootstrapper/setup_chameleon";
import { useDataDogRum } from "PFApp/bootstrapper/use_datadog_rum";
import { useMomentLocale } from "PFApp/bootstrapper/use_moment_locale";
import { useNotifyAboutCustomConfig } from "PFApp/bootstrapper/use_notify_about_custom_config";
import { getGrowlId, useGrowlStoreActions } from "PFApp/stores/growl_store";
import { useSession } from "PFApp/use_session";
import { setupAxiosInstance } from "PFCore/api";
import { useCurrentAccount } from "PFCore/hooks/queries/account/use_current_account";
import { loadClientTranslationOverrides } from "PFCore/i18n/client_translation_overrides";
import { removeLoadingState } from "PFCore/initializers/remove_loading_state";
import { loadDesignTokens } from "PFCore/utilities/load_design_tokens";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useChangeLanguageForAccount } from "./use_change_language";

export const useBootstrapper = () => {
  const { t } = useTranslation();
  const [initialized, setInitialized] = useState(false);

  useNotifyAboutCustomConfig();
  useMomentLocale();
  useDataDogRum();

  useChangeLanguageForAccount();

  const session = useSession();
  const { showGrowl } = useGrowlStoreActions();

  const { data: currentAccount } = useCurrentAccount();

  const { environment, ui_branch: uiBranch, force_ssl: forceSsl } = window.PF.config;

  const handleServerError = () => {
    if (environment && !["integration", "uat", "production"].includes(environment)) {
      showGrowl({
        id: getGrowlId("internal-server-error", "error"),
        message: t("internalServerError"),
        kind: "error"
      });
    }
  };

  const handleQuotaExceeded = ({ featureName, resetsAt }: { featureName: string; resetsAt: string }) => {
    showGrowl({
      id: "QUOTA_EXCEEDED",
      message: t("quotaExceeded", { feature: featureName, date: resetsAt }),
      kind: "error"
    });
  };

  // fetch current profile and set up the rest
  useEffect(() => {
    const fetchCurrentProfileAndSetUp = async () => {
      if (currentAccount.id) {
        setupChameleon(currentAccount);

        const theme = currentAccount.theme || "profinda";
        loadDesignTokens(theme);

        setupAxiosInstance(session, currentAccount.full_domain, handleServerError, handleQuotaExceeded);
        await Promise.all([session.setup(), loadClientTranslationOverrides(currentAccount.clientCodename)]);
        removeLoadingState();
        setInitialized(true);

        if (environment === "staging" && uiBranch !== "staging") {
          showGrowl({
            id: getGrowlId("staging-branch-warning", "alert"),
            message: t("stagingBranchWarning", { branch: uiBranch }),
            kind: "alert"
          });
        }

        if (environment !== "development" && forceSsl !== "false" && location.protocol !== "https:") {
          // @ts-ignore
          window.location = document.URL.replace(/^http:/, "https:");
        }
      }
    };
    fetchCurrentProfileAndSetUp();
  }, [currentAccount.id]); // eslint-disable-line react-hooks/exhaustive-deps

  return { initialized };
};
